import { LocalizedText, useDialogAction } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { TableBulkActionButton } from '@optimus-web/core';
import { IconName } from '@optimus-web/icons';
import { PortfolioVoucherOverview } from '@mews-commander/data-access';
import { createFormResolver } from '@mews-commander/form';
import { getWallClockDateUtcString } from '@mews-commander/utils';
import { validation as validate } from '@mews-commander/legacy-form';
import { useCreateVoucherCodes } from '../../../../../queries';
import { CREATE_VOUCHER_CODE_STARTING_POINT } from '../../../../../utilities/userFlowTestIds';
import { VoucherCodeDialogForm } from './VoucherCodeContainer';
import { DEFAULT_VALUES, FIELD_NAMES, FormValues } from './Form';
import { getConstraints } from './constraints';

interface Props {
    areNoRowsSelected: boolean;
    selectedVouchers: PortfolioVoucherOverview[];
}

export const VoucherCodeWrapper = ({ areNoRowsSelected, selectedVouchers }: Props) => {
    const { states, handlers } = useDialogAction(async () => Promise.resolve());
    const resolver = createFormResolver<FormValues>(validate(getConstraints()));
    const selectedVoucherIds = selectedVouchers.map((voucher) => voucher.id);

    const createVoucherCodes = useCreateVoucherCodes({ selectedVouchers });

    const formProps = {
        resolver,
        defaultValues: {
            ...DEFAULT_VALUES,
        },
    };

    const onSubmit = async (values: FormValues) => {
        await createVoucherCodes.mutateAsync(
            {
                ...values,
                validityStart: getWallClockDateUtcString(values[FIELD_NAMES.VALIDITY_START]),
                validityEnd: getWallClockDateUtcString(values[FIELD_NAMES.VALIDITY_END]),
                voucherIds: selectedVoucherIds,
            },
            {
                onSuccess: async () => {
                    await handlers.handleConfirm();
                },
            }
        );
    };

    return (
        <>
            <TableBulkActionButton
                data-test-id={CREATE_VOUCHER_CODE_STARTING_POINT}
                disabled={areNoRowsSelected}
                icon={IconName.Add}
                onClick={() => handlers.handleOpen()}
            >
                <LocalizedText textKey={TextKey.VoucherCodeAdd} />
            </TableBulkActionButton>

            {states.isDialogOpened && (
                <VoucherCodeDialogForm
                    title={
                        <LocalizedText
                            textKey={TextKey.VoucherCodeTitle}
                            substitutes={{ count: selectedVoucherIds.length }}
                        />
                    }
                    useFormProps={formProps}
                    active={states.isDialogOpened}
                    isLoading={createVoucherCodes.isPending}
                    onConfirm={handlers.handleConfirm}
                    onCancel={handlers.handleCancel}
                    onSubmit={onSubmit}
                />
            )}
        </>
    );
};
